import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CssBaseline from '@mui/material/CssBaseline';
import LinearProgress from '@mui/material/LinearProgress';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PersonIcon from '@mui/icons-material/Person';
import AppTheme from '../shared-theme/AppTheme';
import { useAuth } from '../context/AuthContext';

export default function ProfileSetupPage(props) {
  const navigate = useNavigate();
  const { user, userProfile, updateUserProfile, isAuthenticated } = useAuth();

  const [displayName, setDisplayName] = React.useState('');
  const [bio, setBio] = React.useState('');
  const [website, setWebsite] = React.useState('');
  const [location, setLocation] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Pre-fill with existing data if available
  React.useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
    }
    if (userProfile) {
      setBio(userProfile.bio || '');
      setWebsite(userProfile.website || '');
      setLocation(userProfile.location || '');
    }
  }, [user, userProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate website URL if provided
    const trimmedWebsite = website.trim();
    if (trimmedWebsite) {
      try {
        const url = new URL(trimmedWebsite);
        if (!['http:', 'https:'].includes(url.protocol)) {
          setError('Website must start with http:// or https://');
          return;
        }
      } catch {
        setError('Please enter a valid website URL (e.g. https://example.com)');
        return;
      }
    }

    setLoading(true);

    const result = await updateUserProfile({
      displayName: displayName.trim(),
      bio: bio.trim(),
      website: trimmedWebsite,
      location: location.trim(),
      profileComplete: true,
    });

    if (result.success) {
      navigate('/app');
    } else {
      setError(result.error || 'Failed to save profile');
    }

    setLoading(false);
  };

  const handleSkip = async () => {
    await updateUserProfile({ profileComplete: true });
    navigate('/app');
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Card
          variant="outlined"
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 500,
          }}
        >
          <Stack spacing={3}>
            {/* Header */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                Complete Your Profile
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Tell us a bit about yourself
              </Typography>
            </Box>

            {/* Progress indicator */}
            <Box sx={{ width: '100%' }}>
              <LinearProgress variant="determinate" value={33} sx={{ borderRadius: 1 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                Step 1 of 3: Basic Info
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && <Alert severity="error">{error}</Alert>}

            {/* Profile Picture */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: 'primary.main',
                    fontSize: '2.5rem',
                  }}
                >
                  {displayName ? displayName[0].toUpperCase() : <PersonIcon sx={{ fontSize: 50 }} />}
                </Avatar>
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'background.paper',
                    border: '2px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                    Display Name *
                  </Typography>
                  <TextField
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="How should we call you?"
                    fullWidth
                    required
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                    Bio
                  </Typography>
                  <TextField
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Tell us about yourself and your growing experience..."
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                    Location
                  </Typography>
                  <TextField
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="City, Country"
                    fullWidth
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                    Website / Link
                  </Typography>
                  <TextField
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://your-website.com"
                    fullWidth
                    size="small"
                  />
                </Box>

                <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleSkip}
                    disabled={loading}
                    sx={{
                      flex: 1,
                      py: 1.25,
                      fontFamily: 'Inter, sans-serif',
                      textTransform: 'none',
                    }}
                  >
                    Skip for now
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !displayName.trim()}
                    sx={{
                      flex: 1,
                      py: 1.25,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                  >
                    {loading ? 'Saving...' : 'Continue'}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </Stack>
        </Card>
      </Box>
    </AppTheme>
  );
}
